import type { PinOverride } from '~/features/pin/slice';
import type {
  AlignmentMode,
  BlobCandidate,
  Frame,
  MatchResult,
  Pivot,
  Rect,
} from '~/types/pipeline';
import type { BoxRef } from '~/types/selection';
import { alignFrames } from './align';
import { trimFrame } from './trim';

function pivotToUnitInFrame(pivot: Pivot, cellRect: Rect): Pivot {
  return {
    x: (pivot.x - cellRect.x) / cellRect.w,
    y: (pivot.y - cellRect.y) / cellRect.h,
  };
}

/** Row-major enumeration of all cells in the grid (used when no clip is active). */
export function allCells(rowCount: number, colCount: number): BoxRef[] {
  const out: BoxRef[] = [];
  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < colCount; c++) out.push({ row: r, col: c });
  }
  return out;
}

/** Ordered enumeration of all blob indices, respecting `order` if provided. */
export function allBlobIndices(
  blobs: readonly BlobCandidate[],
  order: readonly number[] | null,
): number[] {
  return order ? [...order] : blobs.map((_, i) => i);
}

/**
 * Build per-cell frames in the order given by `cells`. Each cell becomes one
 * frame; cells whose (row, col) is out-of-range against the divider geometry
 * are skipped.
 */
export function buildFrames(input: {
  cleaned: ImageData;
  cropRect: Rect;
  rows: readonly number[];
  cols: readonly number[];
  alignmentMode: AlignmentMode;
  pivot: Pivot;
  cells: readonly BoxRef[];
  matches?: readonly MatchResult[];
}): Frame[] {
  const colCount = input.cols.length - 1;
  const rowCount = input.rows.length - 1;

  const frames: Frame[] = [];
  let frameIndex = 0;
  for (const { row, col } of input.cells) {
    if (row < 0 || row >= rowCount) continue;
    if (col < 0 || col >= colCount) continue;
    const left = input.cols[col];
    const right = input.cols[col + 1];
    const top = input.rows[row];
    const bottom = input.rows[row + 1];
    if (left === undefined || right === undefined || top === undefined || bottom === undefined) {
      continue;
    }
    const cellRect: Rect = { x: left, y: top, w: right - left, h: bottom - top };
    const trim = trimFrame(input.cleaned, cellRect, 1);
    const trimRect = trim.trimRect;
    frames.push({
      index: frameIndex++,
      cellRect,
      trimRect,
      spriteSourceSize: { x: 0, y: 0, w: trimRect.w, h: trimRect.h },
      sourceSize: { w: cellRect.w, h: cellRect.h },
      pivot: input.pivot,
    });
  }
  return alignFrames(frames, input.alignmentMode, input.matches ? { matches: input.matches } : {});
}

/**
 * Build per-blob frames in the order given by `blobIndices`. Out-of-range or
 * missing blob indices are skipped.
 */
export function buildFramesFromBlobs(input: {
  cleaned: ImageData;
  cropRect: Rect;
  blobs: readonly BlobCandidate[];
  overrides: Record<number, PinOverride>;
  alignmentMode: AlignmentMode;
  blobIndices: readonly number[];
  matches?: readonly MatchResult[];
}): Frame[] {
  const frames: Frame[] = [];
  let frameIndex = 0;
  for (const blobIdx of input.blobIndices) {
    const blob = input.blobs[blobIdx];
    if (!blob) continue;
    const o = input.overrides[blobIdx] ?? {};
    const cellRect = o.bbox ?? blob.bbox;
    const trim = trimFrame(input.cleaned, cellRect, 1);
    const trimRect = trim.trimRect;
    const pivot = o.pivot ?? blob.centroid;
    frames.push({
      index: frameIndex++,
      cellRect,
      trimRect,
      spriteSourceSize: { x: 0, y: 0, w: trimRect.w, h: trimRect.h },
      sourceSize: { w: cellRect.w, h: cellRect.h },
      pivot: pivotToUnitInFrame(pivot, cellRect),
    });
  }
  return alignFrames(frames, input.alignmentMode, input.matches ? { matches: input.matches } : {});
}
